import LoadingTableItems from "@/components/LoadingTableItems";
import ConfirmDeleteButton from "@/components/ConfirmDeleteButton";
import EmptyTableRow from "@/components/EmptyTableRow";
import {notify} from "@/components/Notifications";
import TableSortHeader from "@/components/TableSortHeader";
import TablePagination from "@/components/TablePagination";

import {type SortField, type SortOrder, type TeamMemberPage,} from "@/hooks/useTeamMembers";
import {Pencil} from "lucide-react";

type Props = {
    content: TeamMemberPage;
    isLoading?: boolean;
    isError?: boolean;
    onCreate: () => void;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => Promise<unknown>;
    setPage: (p: number) => void;
    sortBy: SortField;
    sortOrder: SortOrder;
    onRequestSort: (field: SortField) => void;
};

export default function TeamMemberTable({
                                            content,
                                            isLoading,
                                            isError,
                                            setPage,
                                            onCreate,
                                            onEdit,
                                            onDelete,
                                            sortBy,
                                            sortOrder,
                                            onRequestSort,
                                        }: Props) {
    if (isLoading) return <LoadingTableItems/>;
    if (isError) return <p className="mt-6 text-sm text-red-600">Failed to load team members.</p>;

    return (
        <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <div className="overflow-hidden shadow-sm outline-1 outline-black/5 sm:rounded-lg">
                        <table className="relative min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                            <tr>
                                {/* Photo (not sortable) */}
                                <th scope="col"
                                    className="py-3.5 pl-4 pr-3 sm:pl-6 text-left text-sm font-semibold text-blue-900">
                                    Photo
                                </th>

                                <TableSortHeader<SortField>
                                    field="name"
                                    activeField={sortBy}
                                    order={sortOrder}
                                    onSort={onRequestSort}
                                    className="py-3.5 pr-3 pl-3"
                                    thProps={{scope: "col"}}
                                >
                                    Name
                                </TableSortHeader>

                                {/* Description (not sortable, clamped) */}
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-blue-900">
                                    Description
                                </th>

                                <th className="px-3 py-3.5 text-right text-sm font-semibold text-blue-900">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200 bg-white">
                            {content.items.map((m) => (
                                <tr key={m.id}>
                                    {/* Photo */}
                                    <td className="py-4 pl-4 pr-3 sm:pl-6 whitespace-nowrap">
                                        <div
                                            className="h-10 w-10 overflow-hidden rounded-full ring-1 ring-gray-200 bg-gray-50">
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                                src={m.imageUrl}
                                                alt={`${m.name} photo`}
                                                className="h-full w-full object-cover"
                                                onError={(e) => {
                                                    (e.currentTarget as HTMLImageElement).src =
                                                        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><rect width='100%' height='100%' fill='%23f3f4f6'/></svg>";
                                                }}
                                            />
                                        </div>
                                    </td>

                                    {/* Name */}
                                    <td className="px-3 py-4 text-sm font-medium text-blue-900 whitespace-normal">
                                        {m.name}
                                    </td>

                                    {/* Description (line-clamp to keep row compact) */}
                                    <td className="px-3 py-4 text-sm text-gray-500">
                                        <p className="line-clamp-2">{m.description}</p>
                                    </td>

                                    {/* Actions */}
                                    <td className="py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-6">
                                        <div className="inline-flex items-center gap-4">
                                            {/* Edit */}
                                            {onEdit && (
                                                <button
                                                    type="button"
                                                    onClick={() => onEdit(m.id)}
                                                    className="text-yellow-600 hover:text-yellow-500"
                                                    aria-label={`Edit ${m.name}`}
                                                >
                                                    <Pencil/>
                                                </button>
                                            )}

                                            {/* Delete */}
                                            {onDelete && (
                                                <ConfirmDeleteButton
                                                    onDelete={() => onDelete(m.id)}
                                                    itemLabel="Team Member"
                                                    itemName={m.name}
                                                    onSuccess={() => notify.success("Team member deleted.")}
                                                    className="text-red-600 hover:text-red-500"
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {content.items.length === 0 && (
                                <EmptyTableRow
                                    colSpan={4}
                                    title="No team members found"
                                    description='Click “Add Team Member” to create your first.'
                                    action={{label: "Add Team Member", onClick: onCreate}}
                                    size="lg"
                                />
                            )}
                            </tbody>
                        </table>

                        <TablePagination
                            page={content.page}
                            size={content.size}
                            total={content.total}
                            onPage={setPage}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
